import React, { useEffect, useState } from 'react';
import './App.css';
import Header from './components/Header/Header.jsx';
import Profile from './components/Profile/Profile.jsx';
import Dialogs from './components/Dialogs/Dialogs.jsx';
import Users from './components/Users/Users.jsx';
import Groups from './components/Groups/Groups.jsx';
import GroupFeed from './components/Groups/GroupFeed.jsx';
import FGIS from './components/FGIS/FGIS.jsx';
import Login from './components/Login/Login.jsx';
import ProtectedRoute from './components/common/ProtectedRoute.jsx';
import MessageNotifier from './components/common/MessageNotifier.jsx';
import Feed from './components/Feed/Feed.jsx';
import useFullAccess from './components/common/useFullAccess.js';
import PostNotifier from './components/common/PostNotifier.jsx';
import { BrowserRouter, Route, Routes, Navigate } from 'react-router-dom';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { store } from './redux/store';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth, db } from './firebase';
import { doc, setDoc, onSnapshot, getDoc, updateDoc } from 'firebase/firestore';
import { setUser, clearUser, updateUserFields, setAuthReady, SESSION_KEY } from './redux/authSlice';
import logoGzi from './assets/gzi.jpg';

const SESSION_HOURS = 600;
const SESSION_DURATION = SESSION_HOURS * 60 * 60 * 1000;

const isSessionExpired = () => {
  const loginTime = Number(localStorage.getItem(SESSION_KEY) || 0);
  if (!loginTime) {
    localStorage.setItem(SESSION_KEY, String(Date.now()));
    return false;
  }
  return Date.now() - loginTime > SESSION_DURATION;
};

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return isMobile;
};

const LoginGate = () => {
  const isAuth = useSelector((state) => state.auth.isAuth);
  return isAuth ? <Navigate to="/profile" replace /> : <Login />;
};

const AuthListener = ({ children }) => {
  const dispatch = useDispatch();
  const userUid = useSelector((state) => state.auth.user?.uid);
  const userProfile = useSelector((state) => state.auth.user);
  const authReady = useSelector((state) => state.auth.authReady);
  const [authVersion, setAuthVersion] = useState(0);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        if (isSessionExpired()) {
          localStorage.removeItem(SESSION_KEY);
          dispatch(setAuthReady());
          await signOut(auth);
          return;
        }
        try {
          const snap = await getDoc(doc(db, 'users', user.uid));
          if (snap.exists() && snap.data().deleted) {
            await signOut(auth);
            dispatch(clearUser());
            dispatch(setAuthReady());
            alert('Ваш аккаунт был удалён.');
            return;
          }
        } catch (e) {
          console.error('Ошибка проверки статуса:', e);
        }
        await setDoc(doc(db, 'users', user.uid), { uid: user.uid, email: user.email }, { merge: true });
        try {
          const snap = await getDoc(doc(db, 'users', user.uid));
          const profileData = snap.exists() ? snap.data() : {};
          dispatch(setUser({ ...user, ...profileData }));
        } catch (e) {
          dispatch(setUser(user));
        }
        setAuthVersion((v) => v + 1);
      } else {
        dispatch(clearUser());
      }
      dispatch(setAuthReady());
    });
    return () => unsubscribe();
  }, [dispatch]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (auth.currentUser && isSessionExpired()) {
        localStorage.removeItem(SESSION_KEY);
        alert(`Сессия истекла (${SESSION_HOURS} ч). Войдите снова.`);
        signOut(auth);
      }
    }, 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!userUid) return;
    const unsubscribe = onSnapshot(doc(db, 'users', userUid), async (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        dispatch(updateUserFields({ ...data }));
        // Срок статуса истёк — автоматически возвращаем «На работе»
        if (data.statusUntil && data.statusUntil < Date.now() && data.status !== 'На работе') {
          updateDoc(doc(db, 'users', userUid), { status: 'На работе', statusUntil: null }).catch(() => {});
        }
        if (data.deleted) {
          alert('Ваш аккаунт был удалён.');
          await signOut(auth);
        }
      } else {
        alert('Ваш аккаунт был удалён.');
        await signOut(auth);
      }
    });
    return () => unsubscribe();
  }, [userUid, dispatch, authVersion]);

  // Как только срок статуса истёк — сразу возвращаем «На работе»
  useEffect(() => {
    if (userProfile?.statusUntil && userProfile.statusUntil < Date.now() && userProfile.status !== 'На работе') {
      updateDoc(doc(db, 'users', userProfile.uid), { status: 'На работе', statusUntil: null })
        .then(() => dispatch(updateUserFields({ status: 'На работе', statusUntil: null })))
        .catch(() => {});
    }
  }, [userProfile?.statusUntil, userProfile?.status, userProfile?.uid, dispatch]);

  if (!authReady) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#eef1f5', gap: '12px' }}>
          <img
            src={logoGzi}
            alt="ГЖИ"
            style={{ height: '50px', marginBottom: '8px' }}
          />        
        <h2 style={{ color: '#1e3c72', margin: 0 }}>ГЖИ Санкт-Петербурга</h2>
        <p style={{ color: '#888', margin: 0 }}>Проверка сессии...</p>
      </div>
    );
  }

  return children;
};

/* Страница ожидания активации для "Новеньких" */
const PendingActivation = () => (
  <div style={{ padding: '40px', textAlign: 'center', background: 'rgba(255,255,255,0.75)', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
    <p style={{ fontSize: '40px', margin: '0 0 10px 0' }}>🕐</p>
    <h3 style={{ color: '#1e3c72', margin: '0 0 10px 0' }}>Ваш аккаунт «Новенький»</h3>
    <p style={{ color: '#666', margin: '0 0 8px 0' }}>
      Доступ к ленте и списку пользователей откроется, когда Мидл&Boss добавит вас в группу
      или администратор повысит ваши права.
    </p>
    <p style={{ color: '#888', fontSize: '13px', margin: 0 }}>
      Пока доступны: профиль и группы, куда вас добавили.
    </p>
  </div>
);

const AppLayout = () => {
  const isMobile = useIsMobile();
  const { isAuth } = useSelector((state) => state.auth);
  const fullAccess = useFullAccess();

  return (
    <>
      <MessageNotifier />
        <PostNotifier /> 
      <div className="App">
        <Header />
        <div className="app-main">
          <div className="app-content">
            <Routes>
              <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
              <Route path="/dialogs" element={<ProtectedRoute>{fullAccess ? <Dialogs /> : <PendingActivation />}</ProtectedRoute>} />
              <Route path="/dialogs/:userId" element={<ProtectedRoute>{fullAccess ? <Dialogs /> : <PendingActivation />}</ProtectedRoute>} />
              <Route path="/users" element={<ProtectedRoute>{fullAccess ? <Users /> : <PendingActivation />}</ProtectedRoute>} />
              <Route path="/groups" element={<ProtectedRoute><Groups /></ProtectedRoute>} />
              <Route path="/groups/:groupId" element={<ProtectedRoute><GroupFeed /></ProtectedRoute>} />
              <Route path="/fgis" element={<ProtectedRoute><FGIS /></ProtectedRoute>} />
              <Route path="/login" element={<LoginGate />} />
              <Route path="/" element={<Navigate to="/profile" replace />} />
            </Routes>
          </div>
          {/* Лента: только после входа И только при полном доступе */}
          {!isMobile && isAuth && fullAccess && (
            <div className="app-feed-column">
              <Feed />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

const App = () => (
  <Provider store={store}>
    <BrowserRouter>
      <AuthListener>
        <AppLayout />
      </AuthListener>
    </BrowserRouter>
  </Provider>
);

export default App;